import { Photo } from '../types';

export interface LensAggregation {
  totalEstimatedValue: number;
  totalMarketValue: number;
  objectCount: number;       // Photos with completed analysis
  analyzingCount: number;    // Photos currently being analyzed
  belowThreshold: number;    // Objects < 300 SEK
  averageConfidence: number;
  pendingCount: number;      // Photos waiting for analysis
  failedCount: number;       // Photos that failed analysis
}

/**
 * Aggregate AI Lens data across photos in a room/project.
 * Used to show summary values like "7 objekt identifierade, ~12 400 kr"
 */
export function aggregateLensData(photos: Photo[]): LensAggregation {
  let totalEstimatedValue = 0;
  let totalMarketValue = 0;
  let objectCount = 0;
  let analyzingCount = 0;
  let belowThreshold = 0;
  let totalConfidence = 0;
  let pendingCount = 0;
  let failedCount = 0;

  for (const photo of photos) {
    if (!photo.aiLens) continue;

    switch (photo.aiLens.status) {
      case 'completed':
        if (photo.aiLens.result) {
          objectCount++;
          totalConfidence += photo.aiLens.result.confidence || 0;

          // Use market value if available, otherwise AI estimate
          const marketValue = photo.aiLens.market?.medianPrice;
          const aiValue = photo.aiLens.result.estimatedValue;
          const bestValue = marketValue || aiValue;

          if (aiValue) totalEstimatedValue += aiValue;
          if (marketValue) totalMarketValue += marketValue;

          if (bestValue !== undefined && bestValue < 300) {
            belowThreshold++;
          }
        }
        break;

      case 'analyzing':
        analyzingCount++;
        break;

      case 'pending':
        pendingCount++;
        break;

      case 'failed':
        failedCount++;
        break;
    }
  }

  return {
    totalEstimatedValue,
    totalMarketValue,
    objectCount,
    analyzingCount,
    belowThreshold,
    averageConfidence: objectCount > 0 ? totalConfidence / objectCount : 0,
    pendingCount,
    failedCount,
  };
}

/**
 * Format a value in SEK for display.
 */
/** Round down to nearest 100 kr */
export function roundSEK(value: number): number {
  return Math.floor(value / 100) * 100;
}

export function formatSEK(value: number): string {
  const rounded = roundSEK(value);
  return `~${rounded.toLocaleString('sv-SE')} kr`;
}

/**
 * Generate a short summary string from aggregated Lens data.
 * e.g. "7 objekt, ~12 400 kr" or "Analyserar 3 foton..."
 */
export function lensSummaryText(agg: LensAggregation): string | null {
  if (agg.objectCount === 0 && agg.analyzingCount === 0) return null;

  const parts: string[] = [];

  if (agg.objectCount > 0) {
    const value = agg.totalMarketValue > 0
      ? agg.totalMarketValue
      : agg.totalEstimatedValue;

    parts.push(`${agg.objectCount} objekt identifierade`);
    if (value > 0) {
      parts.push(formatSEK(value));
    }
  }

  if (agg.analyzingCount > 0) {
    parts.push(`${agg.analyzingCount} analyseras`);
  }

  return parts.join(', ');
}
